import { useGetAllUsersQuery } from "app/api/users";
import { UserRole } from "features/auth/types/user";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { Office } from "models/Office";
import { useMemo, useState } from "react";
import { CreateOfficeRequest } from "api/offices/createOffice";
import { toast } from "react-toastify";
import {
  useCreateOfficeMutation,
  useDeleteOfficeMutation,
  useUpdateOfficeMutation,
} from "app/api/offices";
import { getErrorMessage } from "utilities/getErrorMessage";
import LargeDialog from "components/LargeDialog";
import { Box, Button, MenuItem, TextField, Tooltip } from "@mui/material";
import Clear from "@mui/icons-material/Clear";
import Check from "@mui/icons-material/Check";
import DeleteOutlined from "@mui/icons-material/DeleteOutlined";
import SimpleDialog from "components/SimpleDialog";
import LoadingDisplay from "components/LoadingDisplay";

type UpsertOfficeDialogProps = {
  office?: Office | null;
  onClose: () => void;
};

const UpsertOfficeDialog: React.FC<UpsertOfficeDialogProps> = (props) => {
  const { office: officeToEdit, onClose } = props;

  const [createOffice, { isLoading: isCreating }] = useCreateOfficeMutation();
  const [updateOffice, { isLoading: isUpdating }] = useUpdateOfficeMutation();
  const [deleteOffice, { isLoading: isDeleting }] = useDeleteOfficeMutation();

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const create = !officeToEdit;

  const { data: admins, isLoading: adminsLoading } = useGetAllUsersQuery({
    roles: [UserRole.ADMIN],
    inactive: true,
  });

  const sortedAdmins = useMemo(() => {
    if (!admins) return [];
    return [...admins].sort((a, b) =>
      a.userName.toLowerCase() > b.userName.toLowerCase() ? 1 : -1
    );
  }, [admins]);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateOfficeRequest>();

  const onSubmit: SubmitHandler<CreateOfficeRequest> = (data) => {
    const requestData = {
      name: data.name,
      orderReviewerId: data.orderReviewerId || null,
    } as CreateOfficeRequest;

    (create ? createOffice(requestData) : updateOffice({ id: officeToEdit!.id, ...requestData }))
      .unwrap()
      .then(() => {
        toast.success(`Kontoret ${create ? "skapades" : "uppdaterades"}`);
        onClose();
      })
      .catch((e) => toast.error(getErrorMessage(e)));
  };

  const onDelete = () => {
    if (!officeToEdit) return;
    deleteOffice({ id: officeToEdit.id })
      .unwrap()
      .then(() => {
        toast.success("Kontoret raderades");
        onClose();
      })
      .catch((e) => toast.error(getErrorMessage(e)));
  };

  return (
    <LargeDialog
      title={create ? "Skapa kontor" : "Redigera kontor"}
      onClose={onClose}
      onSubmit={handleSubmit(onSubmit)}
      actions={
        <>
          <Tooltip
            arrow
            title={officeToEdit?.membersCount! > 0 && "Kan inte radera kontor som har medlemmar"}
          >
            <Box sx={{ mr: "auto" }}>
              <Button
                variant="outlined"
                color="error"
                hidden={create}
                disabled={isCreating || isUpdating || officeToEdit?.membersCount! > 0}
                loading={isDeleting}
                startIcon={<DeleteOutlined />}
                onClick={() => setShowDeleteConfirmation(true)}
              >
                Radera
              </Button>
            </Box>
          </Tooltip>

          <Button
            variant="outlined"
            onClick={onClose}
            disabled={isCreating || isUpdating || isDeleting}
            startIcon={<Clear />}
          >
            Avbryt
          </Button>

          <Button
            type="submit"
            variant="contained"
            disabled={isDeleting}
            loading={isCreating || isUpdating}
            color="success"
            startIcon={<Check />}
          >
            {create ? "Skapa" : "Spara"}
          </Button>
        </>
      }
    >
      {adminsLoading ? (
        <LoadingDisplay message="Laddar användare..." />
      ) : (
        <>
          <Controller
            name="name"
            control={control}
            defaultValue={officeToEdit?.name}
            rules={{ required: "Namn" }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Namn"
                type="text"
                autoComplete="new-password"
                variant="filled"
                error={!!errors.name}
                helperText={errors.name?.message}
              />
            )}
          />

          <Controller
            name="orderReviewerId"
            control={control}
            defaultValue={officeToEdit?.orderReviewer?.userId || ""}
            render={({ field }) => (
              <TextField
                {...field}
                select
                label="Ordergranskare"
                autoComplete="new-password"
                variant="filled"
                type="number"
                error={!!errors.orderReviewerId}
                helperText={errors.orderReviewerId?.message}
              >
                <MenuItem value="" sx={{ color: "gray" }}>
                  Ingen
                </MenuItem>
                {sortedAdmins.map((user) => (
                  <MenuItem key={user.id} value={user.id} hidden={user.state === "INACTIVE"}>
                    {user.userName}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </>
      )}

      {showDeleteConfirmation && (
        <SimpleDialog
          title="Radera kontor?"
          onClose={() => setShowDeleteConfirmation(false)}
          confirmProps={{ color: "error", loading: isDeleting }}
          dismissProps={{ disabled: isDeleting }}
          onConfirm={onDelete}
        />
      )}
    </LargeDialog>
  );
};

export default UpsertOfficeDialog;
